/**
 * LlamigoWidget — floating chat assistant for the Support Portal.
 *
 * Phase 3 skeleton. Contract open items are annotated with TODO(phase3-contract §N):
 *   §5.1 — token refresh pattern (currently: getIdToken on every request)
 *   §5.2 — KB route prefix (currently: LLAMIGO_API_URL/chat, override via VITE_LLAMIGO_URL)
 *   §5.3 — new-ticket pre-fill handoff (currently: URL search params)
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { MessageCircle, X, Send, Ticket, Loader2 } from 'lucide-react'
import { auth } from '../config/firebase'
import { LLAMIGO_API_URL } from '../config/api'
import { supportApi } from '../services/api'

// ─── Types ───────────────────────────────────────────────────────────────────

type Role = 'user' | 'assistant'

interface Message {
  id: string
  role: Role
  content: string
  /** When set, assistant message includes a ticket-handoff action. */
  handoff?: HandoffPayload
}

interface HandoffPayload {
  subject: string
  description: string
  category: string
}

// ─── Llamigo API ─────────────────────────────────────────────────────────────

async function llamigoChat(
  messages: Array<{ role: Role; content: string }>,
  signal: AbortSignal,
  contextSnippet?: string
): Promise<ReadableStreamDefaultReader<Uint8Array>> {
  // TODO(phase3-contract §5.1): confirm whether to force-refresh the token on every call.
  const token = await auth.currentUser?.getIdToken(/* forceRefresh */ true)
  const res = await fetch(`${LLAMIGO_API_URL}/chat`, {
    method: 'POST',
    signal,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      messages,
      // Forward support-portal ticket digest so Llamigo has the user's project + open tickets.
      // Backend reads context_snippet and prepends as a preamble to the system prompt.
      // NOTE: LlamigoMessage in llamigo.py must add context_snippet: Optional[str] to receive this.
      ...(contextSnippet ? { context_snippet: contextSnippet } : {}),
    }),
  })
  if (!res.ok) throw new Error(`Llamigo ${res.status}: ${await res.text()}`)
  if (!res.body) throw new Error('No response body')
  return res.body.getReader()
}

// ─── Support context fetch ────────────────────────────────────────────────────

/**
 * Build a plain-text digest of the user's recent open tickets for the current
 * project. Sent to Llamigo at widget init so it can answer questions like
 * "what are my open tickets?" without the user restating them.
 *
 * Uses projectSlug (short_uuid) to match the project from /my-projects, then
 * fetches the five most recently opened tickets.
 *
 * Returns null on any failure — context is best-effort, never blocking.
 */
async function fetchSupportContext(projectSlug: string): Promise<string | null> {
  try {
    const projects = await supportApi.getMyProjects()
    const project = projects.find((p) => p.short_uuid === projectSlug)
    if (!project) return null

    const tickets = await supportApi.listProjectTickets(project.id, {
      status: 'open',
      limit: 5,
    })

    const parts: string[] = [`Project: ${project.name}`]
    if (tickets.length === 0) {
      parts.push('No open support tickets.')
    } else {
      const summaries = tickets
        .map((t) => `[${t.ticket_number}] ${t.subject} (${t.priority} priority, ${t.category})`)
        .join('; ')
      parts.push(`Open tickets (${tickets.length}): ${summaries}.`)
    }

    return parts.join(' ')
  } catch {
    return null
  }
}

// ─── SSE drain ───────────────────────────────────────────────────────────────

/**
 * Drain a Llamigo SSE response stream, calling onDelta for each token chunk
 * and onHandoff when the backend emits a ticket-handoff action payload.
 * TODO(phase3-contract §5.3): parse "handoff" SSE event type once schema is finalised.
 */
async function drainSSEStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onDelta: (accumulated: string) => void,
  onHandoff: (handoff: HandoffPayload) => void,
): Promise<void> {
  const decoder = new TextDecoder()
  let accumulated = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    const chunk = decoder.decode(value, { stream: true })
    for (const line of chunk.split('\n')) {
      const trimmed = line.startsWith('data: ') ? line.slice(6) : null
      if (!trimmed || trimmed === '[DONE]') continue

      try {
        const parsed: { delta?: string; handoff?: HandoffPayload } = JSON.parse(trimmed)
        if (parsed.delta) {
          accumulated += parsed.delta
          onDelta(accumulated)
        }
        if (parsed.handoff) onHandoff(parsed.handoff)
      } catch {
        // Non-JSON chunk — treat as raw text delta
        accumulated += trimmed
        onDelta(accumulated)
      }
    }
  }
}

// ─── Custom hook ─────────────────────────────────────────────────────────────

/** Fetch and cache the ticket-context digest for the current project slug. */
function useSupportContext(projectSlug?: string): string | null {
  const [digest, setDigest] = useState<string | null>(null)
  useEffect(() => {
    if (!projectSlug) return
    void fetchSupportContext(projectSlug).then((d) => { if (d) setDigest(d) })
  }, [projectSlug])
  return digest
}

// ─── ChatMessage sub-component ────────────────────────────────────────────────

interface ChatMessageProps {
  msg: Message
  onHandoff: (h: HandoffPayload) => void
}

function ChatMessage({ msg, onHandoff }: ChatMessageProps) {
  return (
    <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
          msg.role === 'user'
            ? 'bg-brand-500 text-white rounded-br-sm'
            : 'bg-gray-100 text-gray-800 rounded-bl-sm'
        }`}
      >
        {msg.content || (
          <span className="inline-flex items-center gap-1 text-gray-400">
            <Loader2 className="w-3 h-3 animate-spin" />
            Thinking…
          </span>
        )}
        {msg.handoff && (
          <button
            onClick={() => onHandoff(msg.handoff!)}
            className="mt-2 flex items-center gap-1.5 text-xs font-medium text-brand-600 bg-brand-50 hover:bg-brand-100 border border-brand-200 px-3 py-1.5 rounded-lg transition-colors w-full"
          >
            <Ticket className="w-3.5 h-3.5" />
            Submit a support ticket
          </button>
        )}
      </div>
    </div>
  )
}

// ─── Component ───────────────────────────────────────────────────────────────

export function LlamigoWidget() {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: "Hi! I'm Llamigo, your LlamAssist assistant. How can I help you today?",
    },
  ])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  const navigate = useNavigate()
  const { projectSlug } = useParams<{ projectSlug?: string }>()

  const userDigest = useSupportContext(projectSlug)

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 50) }, [open])
  useEffect(() => () => { abortRef.current?.abort() }, [])

  const sendMessage = async () => {
    const text = input.trim()
    if (!text || streaming) return
    setInput('')
    setError(null)

    const userMsg: Message = { id: crypto.randomUUID(), role: 'user', content: text }
    const assistantId = crypto.randomUUID()
    setMessages((prev) => [...prev, userMsg, { id: assistantId, role: 'assistant', content: '' }])
    setStreaming(true)

    const controller = new AbortController()
    abortRef.current = controller

    try {
      const history = [...messages, userMsg].map((m) => ({ role: m.role, content: m.content }))
      const reader = await llamigoChat(history, controller.signal, userDigest ?? undefined)

      await drainSSEStream(
        reader,
        (acc) => setMessages((prev) =>
          prev.map((m) => m.id === assistantId ? { ...m, content: acc } : m)
        ),
        (handoff) => setMessages((prev) =>
          prev.map((m) => m.id === assistantId ? { ...m, handoff } : m)
        ),
      )
    } catch (err: unknown) {
      if ((err as Error).name === 'AbortError') return
      const msg = err instanceof Error ? err.message : 'Something went wrong'
      setError(msg)
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId
            ? { ...m, content: 'Sorry, I ran into an error. Please try again.' }
            : m
        )
      )
    } finally {
      setStreaming(false)
    }
  }

  // TODO(phase3-contract §5.3): align handoff URL pattern with backend-agent once confirmed.
  const handleHandoff = (handoff: HandoffPayload) => {
    const params = new URLSearchParams({
      subject: handoff.subject,
      description: handoff.description,
      category: handoff.category,
      source: 'llamigo',
    })
    navigate(`/${projectSlug ?? ''}/new?${params.toString()}`)
    setOpen(false)
  }

  return (
    <>
      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-20 right-4 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col z-50"
          style={{ maxHeight: 'calc(100vh - 120px)' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-brand-500 rounded-t-2xl">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-white" />
              </div>
              <span className="text-white font-semibold text-sm">Llamigo</span>
              <span className="text-white/60 text-xs ml-1">AI assistant</span>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/70 hover:text-white transition-colors"
              aria-label="Close chat"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-0">
            {messages.map((msg) => (
              <ChatMessage key={msg.id} msg={msg} onHandoff={handleHandoff} />
            ))}
            {error && <p className="text-xs text-red-500 text-center">{error}</p>}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="px-3 pb-3 pt-2 border-t border-gray-100">
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => { e.preventDefault(); void sendMessage() }}
            >
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask Llamigo…"
                disabled={streaming}
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={streaming || !input.trim()}
                className="p-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label="Send"
              >
                {streaming ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Floating toggle button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={`fixed bottom-4 right-4 rounded-full shadow-lg flex items-center justify-center transition-all z-50 ${
          open ? 'bg-gray-700 hover:bg-gray-800' : 'bg-brand-500 hover:bg-brand-600'
        }`}
        style={{ width: 52, height: 52 }}
        aria-label={open ? 'Close Llamigo chat' : 'Open Llamigo chat'}
      >
        {open ? (
          <X className="w-5 h-5 text-white" />
        ) : (
          <MessageCircle className="w-5 h-5 text-white" />
        )}
      </button>
    </>
  )
}
